import React, { useMemo, useState } from "react";
import { Modal, Select } from "antd";
import AgGridLayout from "../wrappers/layouts/AgGridLayout";
import { ColDef, RowClassParams } from "ag-grid-community";
import { customFilterOptions, TAG_COLORS } from "@ts/constants";
import { titleCase } from "@ts/utils";
import { ITagMappingTableStats, TagStatusFilters } from "@ts/common-types";

type IPfmTagsStatusModalProps = {
    show: boolean;
    setShow: (show: boolean) => void;
    data: {
        table_data_visibility: ITagMappingTableStats[];
        table_data_upd: ITagMappingTableStats[];
    };
};

const PfmTagsStatusModal: React.FC<IPfmTagsStatusModalProps> = ({ show, setShow, data }) => {
    const [selectedMetric, setSelectedMetric] = useState<TagStatusFilters>(TagStatusFilters.VISIBILITY_SCORE);
    const { table_data_visibility, table_data_upd } = data;

    const tableStats = useMemo(() => 
        selectedMetric === TagStatusFilters.VISIBILITY_SCORE ? table_data_visibility : table_data_upd, 
        [data, selectedMetric]
    );

    const onClose = () => {
        setShow(false);
    }

    const rowData = useMemo(() => {
        if (!tableStats) return [];

        const allChannels = Array.from(new Set(tableStats.map(item => item.channel_name)));
        const groupedByTagType = tableStats.reduce((acc, item) => {
            if (!acc[item.tag_type]) {
                acc[item.tag_type] = {};
                allChannels.forEach(channel => {
                    acc[item.tag_type][channel] = "0.00%";
                });
            }
            acc[item.tag_type][item.channel_name] = `${item.tag_perc.toFixed(2)}%`;
            return acc;
        }, {} as Record<string, Record<string, string>>);

        return Object.entries(groupedByTagType).map(([tag_type, channels]) => ({
            field: tag_type,
            ...channels
        }));
    }, [tableStats]);

    const columnDefs: ColDef[] = useMemo(() => {
        if (rowData.length === 0) return [];

        const channelNames = Object.keys(rowData[0]).filter((k) => k !== "field");

        return [
            { 
                headerName: "Tag Type", 
                field: "field", 
                pinned: "left", 
                valueFormatter: (params) => {
                    return titleCase(params.value);
                },
                cellStyle: { fontWeight: 'bold' },
                filter: 'agTextColumnFilter',
                filterParams: {
                    filterOptions: customFilterOptions.text,
                }
            },
            ...channelNames.map((channel) => ({
                headerName: titleCase(channel),
                field: channel,
                filter: 'agNumberColumnFilter',
                filterParams: {
                    filterOptions: customFilterOptions.numberWithoutChanges
                }
            })),
        ];
    }, [rowData]);

    const getRowStyle = (params: RowClassParams) => {
        const field = params.data.field.toLowerCase();
        const colors = {
            category: TAG_COLORS[0].background,
            subcategory: TAG_COLORS[1].background,
            brand: TAG_COLORS[2].background
        };
        
        // For other tag types, use a color index based on row position
        // Assuming first 3 are global (category, subcategory, brand), start from index 3
        const colorIndex = params.node.rowIndex > 2 ? (params.node.rowIndex - 3) % TAG_COLORS.length : params.node.rowIndex;
        return { background: colors[field as keyof typeof colors] || TAG_COLORS[colorIndex].background };
    };

    const metricOptions = [
        { label: "Visibility Score", value: TagStatusFilters.VISIBILITY_SCORE },
        { label: "Sales Index", value: TagStatusFilters.SALES_INDEX }
    ];

    return (
        <Modal
            title={
                <div className="d-flex justify-content-between align-items-center pe-4">
                    <span>PFM Tags Status</span>
                    <Select
                        className="me-2"
                        value={selectedMetric}
                        onChange={setSelectedMetric}
                        options={metricOptions}
                        style={{ width: 200 }}
                    />
                </div>
            }
            width={1000}
            open={show}
            onOk={onClose}
            onCancel={onClose}
            footer={null}
            style={{ top: 130 }}
        >
            <div
                id="pfm-tags-status-grid"
                className="ag-theme-alpine product-mapping-grid"
                style={{
                    height: "500px",
                    width: "100%",
                    flex: "1",
                }}
            >
                <AgGridLayout
                    columnDefs={columnDefs}
                    rowData={rowData}
                    rowHeight={64}
                    pagination={false}
                    isRowSelectable={() => false}
                    isTotalRowsReq={false}
                    getRowStyle={getRowStyle}
                    setGridApi={() => {}}
                />
            </div>
        </Modal>
    );
};

export default PfmTagsStatusModal;